import React, { useCallback, useContext, useEffect, useState } from 'react'
import { shallow } from 'zustand/shallow'
import { isEqual } from 'lodash'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useHasPermissions } from 'react-simple-rbac'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useApiErrorMessage } from '../../../i18n/useApiErrorMessage'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import Layout from '../../Layout/Layout'
import InformationModal from '../../Modals/ConfirmationModal/InformationModal'
import HtmlTooltip from '../../HtmlTooltip/HtmlTooltip'
import PriorityChainModal from './PriorityChainModal'

import Chip from '@mui/material/Chip'

import { MaterialIcons } from '../../../icons/Material/MaterialIcons'

import {
  cancelNoveltyProcessing,
  editCompanyNovelty,
  getCompanyNoveltiesTypes,
  getCompanyNoveltyById,
  getNoveltyLogs,
  getPriorityChain,
  processNovelty,
  unblockNovelty,
} from '../../../services/CompaniesService'

import {
  approveNovelty,
  downloadNoveltyFile,
  rejectNovelty,
} from '../../../services/ParametersService'

import { UserContext } from '../../../context/user/UserContext'

import { useEmpresaStore } from '../../../store/empresaStore'
import FetchErrorMessage from '../../FetchErrorMessage/FetchErrorMessage'
import { useNoveltiesStore } from '../../../store/noveltiesStore'
import { TextareaAutosize } from '@mui/material'
import { BasicUserData, CompanyNoveltiesType } from '../../../types'

import {
  NOVELTY_STATUS_VALUES,
  useNoveltyStatusLabel,
} from '../../../i18n/useNoveltyStatusLabel'

import {
  getInterfaceCode,
  validateNoveltyFileName,
} from '../../Novedades/consts/crear-novedad-consts'

type PreviusStateType = {
  file: string
  fileName: string
  name: string
  novelty_type_id: string
  period_id: string
  priority: number
  urgent: boolean
  status: number
  created_by: string
  error: string
  result: string
  process_date: string
}
type InitialValues = {
  name: string
  period_id: string
  novelty_type_id: string
  fileName: string
  file: string
  urgent: boolean
  status: number
  priority: number
  created_by: string
  created_at: string
  error: string
  result: string
  process_date: string
  rejection_reason: string
  approved_by: BasicUserData | null
  approved_at: string
  created_by_user: BasicUserData | null
}
type NovedadDetailsType = {
  params: any
}

const NovedadDetails: React.FC<NovedadDetailsType> = ({ params }) => {
  const { t } = useTranslation('novedadDetails')
  const getApiErrorMessage = useApiErrorMessage()
  const getStatusLabel = useNoveltyStatusLabel()
  const [clickCancelar, setClickCancelar] = useState<boolean>(false)
  const [clickGuardar, setClickGuardar] = useState<boolean>(false)
  const [clickCancelarProcesamiento, setClickCancelarProcesamiento] =
    useState<boolean>(false)
  const [clickProcesar, setClickProcesar] = useState<boolean>(false)
  const [clickDesbloquear, setClickDesbloquear] = useState<boolean>(false)
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [openInformationModal, setOpenInformationModal] = useState<boolean>(false)
  const [openProcessingModal, setOpenProcessingModal] = useState<boolean>(false)
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false)
  const [openUnblockModal, setOpenUnblockModal] = useState<boolean>(false)
  const [openApproveModal, setOpenApproveModal] = useState<boolean>(false)
  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false)
  const [openPriorityChainModal, setOpenPriorityChainModal] = useState<boolean>(false)
  const [priorityChain, setPriorityChain] = useState<CompanyNoveltiesType[]>([])
  const [rejectReason, setRejectReason] = useState<string>('')
  const [disabledDownload, setDisabledDownload] = useState<boolean>(false)
  const [disabledDownloadLogs, setDisabledDownloadLogs] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [previusState, setPreviusState] = useState<PreviusStateType>({
    file: '',
    fileName: '',
    name: '',
    novelty_type_id: '',
    period_id: '',
    priority: 0,
    urgent: false,
    status: 1,
    created_by: '',
    error: '',
    result: '',
    process_date: '',
  })
  const [isErrorProcessedOrCancelled, setIsErrorProcessedOrCancelled] =
    useState<boolean>(true)
  // "Subir nuevamente" desde la tabla de cadena de prioridad trae ?reupload=1:
  // habilita puntualmente Tipo y Archivo para reintentar una novedad en Error,
  // sin abrir el resto del formulario (nombre/período/prioridad/urgente).
  const [searchParams] = useSearchParams()
  const isReuploadMode = searchParams.get('reupload') === '1'
  // Solo se habilita si la novedad efectivamente está en Error (status 4): el
  // query param por sí solo no debe abrir edición en ningún otro estado.
  const canReuploadThis = isReuploadMode && previusState.status === 4
  // Novedad que abrió la cadena de prioridad y a la que hay que volver tras
  // guardar un reupload, reabriendo el modal para continuar procesando.
  const anchorNoveltyId = searchParams.get('anchor')
  const shouldReopenChain = searchParams.get('openChain') === '1'
  const [disabledOnSubmit, setDisabledOnSubmit] = useState<boolean>(false)
  const [showIcon, setShowIcon] = useState<boolean>(false)
  const [successIcon, setSuccessIcon] = useState<boolean>(false)
  const [refreshNovelty, setRefreshNovelty] = useState<boolean>(false)
  const [fetchError, setFetchError] = useState<boolean>(false)
  const [fetchErrorMessage, setFetchErrorMessage] = useState<string>('')
  const [actualEmpresaNovedades, setActualEmpresaNovedades] = useState<any[]>([])
  const [loadNoveltyError, setLoadNoveltyError] = useState<boolean>(false)
  const [loadNoveltyErrorMessage, setLoadNoveltyErrorMessage] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [isNoveltyProcessing, setIsNoveltyProcessing] = useState<boolean>(false)
  const [isProcessingMessage, setIsProcessingMessage] = useState<string>('')

  const userCanUnblock = useHasPermissions(['unblock:novelty'])
  const userCanApprove = useHasPermissions(['update:novelty'])

  const timer = React.useRef<number>()

  const navigate = useNavigate()

  const { getAccessTokenSilently } = useAuth0()

  const { user } = useContext(UserContext)
  const { allEmpresaPeriodos, empresaId, selectedEmpresa } = useEmpresaStore(
    ({ allEmpresaPeriodos, empresaId, selectedEmpresa }) => ({
      allEmpresaPeriodos,
      empresaId,
      selectedEmpresa,
    }),
    shallow,
  )

  const noveltiesStore = useNoveltiesStore(state => state.novedades, shallow)

  const { eliminarNovedad, setNovedadProcesada } = useNoveltiesStore(
    ({ eliminarNovedad, setNovedadProcesada }) => ({
      eliminarNovedad,
      setNovedadProcesada,
    }),
  )

  const formik = useFormik<InitialValues>({
    initialValues: {
      name: '',
      period_id: '',
      novelty_type_id: '',
      fileName: '',
      file: '',
      urgent: false,
      status: 0,
      priority: 0,
      created_by: '',
      created_at: '',
      error: '',
      result: '',
      process_date: '',
      rejection_reason: '',
      approved_by: null,
      approved_at: '',
      created_by_user: null,
    },
    validationSchema: Yup.object({
      fileName: Yup.mixed().test(
        'filename-matches-novelty-type',
        t('novelty.fileNameTypeMismatch', { ns: 'validation' }),
        function (value) {
          if (!value) return true
          const code = getInterfaceCode(this.parent.novelty_type_id, actualEmpresaNovedades)
          return validateNoveltyFileName(String(value), code)
        },
      ),
    }),
    onSubmit: async data => {
      setDisabledOnSubmit(true)
      let finalData = {}
      if (data.file !== previusState.file) {
        finalData = {
          file: data.file,
          name: data.name,
          period_id: data.period_id,
          company_id: empresaId,
          novelty_type_id: data.novelty_type_id,
          urgent: data.urgent,
          priority: data.priority,
          status: data.status,
        }
      } else {
        finalData = {
          name: data.name,
          period_id: data.period_id,
          company_id: empresaId,
          novelty_type_id: data.novelty_type_id,
          urgent: data.urgent,
          priority: data.priority,
          status: data.status,
        }
      }

      const token = await getAccessTokenSilently()

      editCompanyNovelty(token, empresaId, params.novedadId, finalData)
        .then(result => {
          if (result.status === 201 || result.status === 200) {
            handleCloseModal()
            formik.resetForm()
            // Si veníamos de "Subir nuevamente", volver a la novedad que abrió
            // la cadena de prioridad y reabrirla, para continuar el flujo sin
            // que se sienta como un corte.
            if (canReuploadThis && anchorNoveltyId) {
              navigate(`/novedades/novedad/${anchorNoveltyId}?openChain=1`)
            } else {
              navigate(-1)
            }
          }
        })
        .catch(e => {
          console.error(e.response.data.error)
          setErrorMessage(getApiErrorMessage(e))
          setDisabledOnSubmit(false)
          setOpenErrorModal(true)
        })
    },
  })

  // Verificar si el usuario es Client Basic y la novedad requiere aprobación
  const isClientBasic = user?.role === 3
  const isBDOAdmin = user?.role === 2
  // Solo BDO Administrador (2) y BDO Básico (4) ven la tabla guiada de cadena de prioridad.
  const canSeePriorityTable = user?.role === 2 || user?.role === 4
  const hasPayrollConfig = selectedEmpresa && 'payrollConfig' in selectedEmpresa
  const requiresApproval =
    isClientBasic &&
    hasPayrollConfig &&
    selectedEmpresa.payrollConfig?.requiresNoveltyApproval === true
  const handleCloseModal = () => {
    setClickCancelarProcesamiento(false)
    setClickProcesar(false)
    setOpenInformationModal(false)
    openErrorModal && setOpenErrorModal(false)
    openProcessingModal && setOpenProcessingModal(false)
    openUnblockModal && setOpenUnblockModal(false)
    setOpenApproveModal(false)
    setOpenRejectModal(false)
    setRejectReason('')
  }

  const handleIsProcessingCloseModal = () => {
    if (!isProcessing) {
      setOpenProcessingModal(false)
      setIsProcessing(false)
      setClickCancelarProcesamiento(false)
      setShowIcon(false)
      setSuccessIcon(false)
      setErrorMessage('')
      setDisabledOnSubmit(false)
      setRefreshNovelty(true)
    }
  }

  const handleDeshacerCancelar = () => {
    setClickCancelar(true)
    setClickGuardar(false)
    setClickCancelarProcesamiento(false)
    setClickProcesar(false)
    setOpenUnblockModal(false)
    formik.resetForm()
    navigate(-1)
  }

  const handleClickDirtyCancelar = () => {
    setClickGuardar(false)
    setClickCancelarProcesamiento(false)
    setClickProcesar(false)
    setOpenInformationModal(true)
    setIsDirty(true)
    setClickCancelar(true)
  }

  const handleClickGuardar = () => {
    setIsDirty(false)
    setClickCancelar(false)
    setClickGuardar(true)
    setOpenInformationModal(true)
  }

  const handleClickCancelNoveltyProccessing = () => {
    setClickProcesar(false)
    setIsDirty(false)
    setClickCancelar(false)
    setClickGuardar(false)
    setClickCancelarProcesamiento(true)
    setOpenProcessingModal(true)
  }

  const handleClickUnblockNovelty = () => {
    setClickProcesar(false)
    setIsDirty(false)
    setClickCancelar(false)
    setClickGuardar(false)
    setClickCancelarProcesamiento(false)
    setOpenProcessingModal(false)
    setClickDesbloquear(true)
    setOpenUnblockModal(true)
  }

  const handleUploadFile = (file: any) => {
    formik.setValues({ ...formik.values, file, fileName: file.name })
    formik.setFieldTouched('fileName', true, false)
  }

  const handleChangeNoveltyType = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e)
    // Cambiar el tipo puede dejar de coincidir con el archivo ya cargado (ver
    // validationSchema.fileName): se marca tocado para que el mensaje de error
    // se muestre de inmediato, en vez de quedar solo bloqueado el boton Guardar.
    if (formik.values.fileName) {
      formik.setFieldTouched('fileName', true, false)
    }
  }

  const handleSaveButton = () => {
    formik.submitForm()
  }

  const getNovedadLogs = async () => {
    setDisabledDownloadLogs(true)
    const token = await getAccessTokenSilently()
    getNoveltyLogs(token, empresaId, params.novedadId)
      .then(result => {
        // Create blob link to download
        const link = document.createElement('a')
        const url = window.URL.createObjectURL(
          new Blob([result.data, formik.values.error]),
        )
        link.href = url
        link.setAttribute('download', `${previusState.name} - logs`)

        // Append to html link element page
        document.body.appendChild(link)

        // Start download
        link.dispatchEvent(new MouseEvent('click'))

        // Clean up and remove the link
        link.parentNode?.removeChild(link)
        setDisabledDownloadLogs(false)
      })
      .catch(e => {
        console.error('error', e.response.data.error)
        //  Download the error as log
        if (formik.values.error) {
          // Create blob link to download
          const link = document.createElement('a')
          const url = window.URL.createObjectURL(new Blob([formik.values.error]))
          link.href = url
          link.setAttribute('download', `${previusState.name} - logs`)

          // Append to html link element page
          document.body.appendChild(link)

          // Start download
          link.dispatchEvent(new MouseEvent('click'))

          // Clean up and remove the link
          link.parentNode?.removeChild(link)
          setDisabledDownloadLogs(false)
        } else {
          setErrorMessage(getApiErrorMessage(e))
          setDisabledDownloadLogs(false)
          setOpenErrorModal(true)
        }
      })
  }
  const downloadArch = async () => {
    setDisabledDownload(true)
    const token = await getAccessTokenSilently()
    const result = await downloadNoveltyFile(token, previusState.file)

    // Create blob link to download
    const link = document.createElement('a')
    const url = window.URL.createObjectURL(new Blob([result.data]))
    link.href = url
    link.setAttribute('download', previusState.fileName)

    // Append to html link element page
    document.body.appendChild(link)

    // Start download
    link.dispatchEvent(new MouseEvent('click'))

    // Clean up and remove the link
    link.parentNode?.removeChild(link)
    setDisabledDownload(false)
  }

  const handleChangePriority = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (Number(e.target.value) >= 0) {
      formik.setFieldValue('priority', Number(e.target.value))
    }
  }

  // Consulta la cadena de prioridad de una novedad y, si está bloqueada, abre
  // el modal guiado (solo BDO) o un error genérico. Se reusa tanto al apretar
  // "Procesar ahora" como al volver de un reupload para reabrir la cadena en
  // el mismo punto donde el usuario la dejó.
  const openPriorityChainFor = async (noveltyId: string) => {
    try {
      const token = await getAccessTokenSilently()
      const res = await getPriorityChain(token, empresaId, noveltyId)
      if (res.data.blocked) {
        if (canSeePriorityTable) {
          setPriorityChain(res.data.chain)
          setOpenPriorityChainModal(true)
        } else {
          setErrorMessage(t('errors.priorityConflict'))
          setOpenErrorModal(true)
        }
        return true
      }
    } catch (e) {
      // Si la consulta de cadena falla, no bloqueamos el flujo normal de procesamiento.
      console.error('Error al consultar la cadena de prioridad', e)
    }
    return false
  }

  const handleClickProcesar = async () => {
    // Antes de procesar, validar el orden de prioridad de la cadena del período.
    const blocked = await openPriorityChainFor(params.novedadId)
    if (blocked) {
      return
    }

    setIsDirty(false)
    setClickCancelar(false)
    setClickGuardar(false)
    setClickCancelarProcesamiento(false)
    setClickProcesar(true)
    setOpenProcessingModal(true)
  }

  const handleClickApprove = () => {
    setOpenApproveModal(true)
  }

  const handleClickReject = () => {
    setOpenRejectModal(true)
  }

  const handleApproveNovelty = async () => {
    setIsProcessing(true)
    setShowIcon(false)
    setSuccessIcon(false)
    try {
      const token = await getAccessTokenSilently()
      await approveNovelty(token, empresaId as string, params.novedadId)
      setSuccessIcon(true)
      setIsProcessingMessage(t('errors.approveSuccess'))
      setTimeout(() => {
        setIsProcessing(false)
        setOpenApproveModal(false)
        setRefreshNovelty(true)
      }, 1500)
    } catch (error: any) {
      setIsProcessing(false)
      setErrorMessage(getApiErrorMessage(error))
      setOpenApproveModal(false)
      setOpenErrorModal(true)
    }
  }

  const handleRejectNovelty = async () => {
    if (!rejectReason.trim()) {
      setErrorMessage(t('errors.rejectReasonRequired'))
      setOpenErrorModal(true)
      return
    }

    setIsProcessing(true)
    setShowIcon(false)
    setSuccessIcon(false)
    try {
      const token = await getAccessTokenSilently()
      await rejectNovelty(token, empresaId as string, params.novedadId, rejectReason)
      setSuccessIcon(true)
      setIsProcessingMessage(t('errors.rejectSuccess'))
      setTimeout(() => {
        setIsProcessing(false)
        setOpenRejectModal(false)
        setRejectReason('')
        setRefreshNovelty(true)
      }, 1500)
    } catch (error: any) {
      setIsProcessing(false)
      setErrorMessage(getApiErrorMessage(error))
      setOpenRejectModal(false)
      setOpenErrorModal(true)
    }
  }

  const handleSaveForm = () => {
    clickCancelarProcesamiento && handleCancelNoveltyProccessing()
    clickProcesar && handleProcessNovelty()
    clickDesbloquear && handleUnblockNovelty()
  }

  const handleUnblockNovelty = async () => {
    setDisabledOnSubmit(true)
    setShowIcon(false)
    const token = await getAccessTokenSilently()
    unblockNovelty(token, empresaId, params.novedadId)
      .then(res => {
        console.table(res)
        setClickDesbloquear(false)
        setDisabledOnSubmit(false)
        navigate(-1)
      })
      .catch(e => {
        console.error(e.response.data.error)
        setErrorMessage(t('errors.unblockError'))
        setDisabledOnSubmit(false)
        setOpenErrorModal(true)
      })
  }

  const handleCancelNoveltyProccessing = async () => {
    setDisabledOnSubmit(true)
    setShowIcon(false)
    const token = await getAccessTokenSilently()
    cancelNoveltyProcessing(token, empresaId, params.novedadId, formik.values)
      .then(res => {
        console.table(res)
        setClickCancelarProcesamiento(false)
        setDisabledOnSubmit(false)
        navigate(-1)
      })
      .catch(e => {
        console.error(e.response.data.error)
        setErrorMessage(t('errors.cancelProcessingError'))
        setDisabledOnSubmit(false)
        setOpenErrorModal(true)
      })
  }

  const getActualEmpresaNovedades = useCallback(
    async (actEmpresaId: string) => {
      setIsLoading(true)
      const token = await getAccessTokenSilently()
      getCompanyNoveltiesTypes(token, actEmpresaId)
        .then(novedades => {
          if (novedades.data) {
            if (novedades.data.length > 0) {
              setActualEmpresaNovedades(novedades.data)
            } else {
              setActualEmpresaNovedades([])
            }
          }
          setIsLoading(false)
        })
        .catch(e => {
          console.error(e.response.data.error)
          setFetchError(true)
          setFetchErrorMessage(getApiErrorMessage(e))
          setActualEmpresaNovedades([])
          setIsLoading(false)
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const handleProcessNovelty = async () => {
    setDisabledOnSubmit(true)
    setIsProcessing(true)
    const token = await getAccessTokenSilently()
    processNovelty(token, empresaId, { id: params.novedadId })
      .then(res => {
        setNovedadProcesada(params.novedadId, true)
        setIsProcessingMessage(res.data.message)
        setIsProcessing(false)
        setClickCancelarProcesamiento(false)
        setShowIcon(true)
        setSuccessIcon(true)
        timer.current = window.setTimeout(() => {
          setShowIcon(false)
          setSuccessIcon(false)
          handleCloseModal()
          setDisabledOnSubmit(false)
          setRefreshNovelty(true)
        }, 1000)
        // navigate(-1);
      })
      .catch(e => {
        console.error('ERROR', e.request.status)
        setIsProcessing(false)
        setNovedadProcesada(params.novedadId, false)
        if (e.request.status === 0) {
          setErrorMessage(t('errors.cors'))
        } else if (e.response?.data?.error_code) {
          setErrorMessage(getApiErrorMessage(e))
        } else {
          setErrorMessage(t('errors.processError'))
        }
        setShowIcon(true)
        setSuccessIcon(false)
      })
  }

  const getEmpresaNovedadById = useCallback(async () => {
    setIsLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const result = await getCompanyNoveltyById(token, empresaId, params.novedadId)

      setPreviusState({
        file: result.data.file.ref,
        fileName: result.data.file.name,
        name: result.data.name,
        novelty_type_id: result.data.novelty_type_id,
        period_id: result.data.period_id,
        priority: result.data.priority,
        urgent: result.data.urgent,
        status: result.data.status,
        created_by: result.data.created_by,
        error: result.data.error ? result.data.error : '',
        result: result.data.result ? result.data.result : '',
        process_date: result.data.process_date ? result.data.process_date : '',
      })

      formik.setValues({
        file: result.data.file.ref,
        fileName: result.data.file.name,
        name: result.data.name,
        novelty_type_id: result.data.novelty_type_id,
        period_id: result.data.period_id,
        priority: result.data.priority,
        urgent: result.data.urgent,
        status: result.data.status,
        created_by: result.data.created_by,
        created_at: result.data.created_at ?? '',
        error: result.data.error ? result.data.error : '',
        result: result.data.result ? result.data.result : '',
        process_date: result.data.process_date ? result.data.process_date : '',
        rejection_reason: result.data?.rejection_reason
          ? result.data?.rejection_reason
          : '',
        approved_by: result.data?.approved_by,
        approved_at: result.data?.approved_at ? result.data?.approved_at : '',
        created_by_user: result.data?.user ?? null,
      })

      setIsErrorProcessedOrCancelled(
        result.data.status === 2 || result.data.status === 4 || result.data.status === 5,
      )

      if (
        result.data.status === 2 ||
        result.data.status === 4 ||
        result.data.status === 5
      ) {
        eliminarNovedad(params.novedadId)
      }

      setIsLoading(false)
      setRefreshNovelty(false)

      // Al volver de guardar un reupload (?openChain=1&anchor=<id>), reabrir la
      // cadena de prioridad acá mismo, justo cuando la novedad ancla terminó
      // de cargar — evita la carrera de depender de un useEffect separado con
      // isLoading como proxy, que puede quedar desfasado si esta página no se
      // remonta (misma ruta, solo cambia el param) al volver del reupload.
      if (shouldReopenChain && anchorNoveltyId && anchorNoveltyId === params.novedadId) {
        openPriorityChainFor(anchorNoveltyId)
      }
    } catch (error: any) {
      console.error('Error al realizar la petición:', error?.response?.data?.error)
      setLoadNoveltyErrorMessage(getApiErrorMessage(error))
      setFetchError(true)
      setLoadNoveltyError(true)
      setIsLoading(false)
      setRefreshNovelty(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, params.novedadId])

  useEffect(() => {
    if (noveltiesStore[params.novedadId]) {
      setIsNoveltyProcessing(true)
    }
  }, [noveltiesStore, params.novedadId])

  // Si se llega vía "Subir nuevamente" (?reupload=1), esta página abre en su
  // propio detalle: no debe arrastrar el modal de cadena que estaba abierto
  // en la novedad de origen.
  useEffect(() => {
    if (isReuploadMode) {
      setOpenPriorityChainModal(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReuploadMode])

  useEffect(() => {
    return () => {
      clearTimeout(timer.current)
    }
  }, [])

  useEffect(() => {
    if (refreshNovelty) {
      getEmpresaNovedadById()
      getActualEmpresaNovedades(empresaId)
    }
  }, [empresaId, getActualEmpresaNovedades, getEmpresaNovedadById, refreshNovelty])

  useEffect(() => {
    getEmpresaNovedadById()
    getActualEmpresaNovedades(empresaId)
  }, [empresaId, getActualEmpresaNovedades, getEmpresaNovedadById])

  useEffect(() => {
    document.title = `${window._env_.REACT_APP_EMPRESA_NAME} - ${t('pageTitle')}`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout icon="PollIcon" title={t('pageTitle')} breadcrumb>
      <Grid container marginTop="12px" borderRadius="6px">
        <Grid container marginTop="12px" borderRadius="6px">
          <Grid bgcolor="white" item xs={8} borderRadius="6px">
            {isLoading ? (
              <Box
                sx={{
                  borderRadius: '6px',
                  display: 'flex',
                  backgroundColor: 'white',
                  height: '100%',
                  width: '100%',
                  paddingRight: '18px',
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Box
                    id="create-novelty-form"
                    borderRadius="6px"
                    sx={{
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '461px',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </Box>
              </Box>
            ) : fetchError || loadNoveltyError ? (
              <FetchErrorMessage
                errorMessage={fetchErrorMessage || loadNoveltyErrorMessage}
              />
            ) : (
              <>
                <Box
                  sx={{
                    borderRadius: '6px',
                    display: 'flex',
                    backgroundColor: 'white',
                    height: '100%',
                    width: '100%',
                    paddingRight: '18px',
                  }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Box
                      id="create-novelty-form"
                      component="form"
                      autoComplete="off"
                      borderRadius="6px"
                      sx={{ backgroundColor: 'white', height: '100%' }}
                      onSubmit={formik.handleSubmit}
                    >
                      <Grid container>
                        <Grid item xs={6} sx={{ pt: '18px', pb: '10px', pl: '18px' }}>
                          <Box
                            display="flex"
                            justifyContent="flex-start"
                            marginBottom="4px"
                          >
                            <InputLabel sx={{ color: 'black' }} htmlFor="nombre-input">
                              {t('fields.name')}
                            </InputLabel>
                          </Box>
                          <TextField
                            id="nombre-input"
                            error={Boolean(formik.touched.name && formik.errors.name)}
                            fullWidth
                            helperText={formik.touched.name && formik.errors.name}
                            name="name"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            type="text"
                            value={formik.values.name}
                            variant="filled"
                            placeholder={t('fields.namePlaceholder')}
                            InputProps={{
                              disableUnderline: true,
                              readOnly:
                                isErrorProcessedOrCancelled || isNoveltyProcessing,
                              sx: {
                                borderRadius: '6px',
                                '.MuiInputBase-input': {
                                  paddingBottom: '16px',
                                  paddingTop: '16px',
                                },
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={6} sx={{ pt: '18px', pb: '10px', pl: '18px' }}>
                          <Box
                            display="flex"
                            justifyContent="flex-start"
                            marginBottom="4px"
                          >
                            <InputLabel sx={{ color: 'black' }} htmlFor="type-select">
                              {t('fields.type')}
                            </InputLabel>
                          </Box>
                          <TextField
                            id="type-select"
                            select
                            error={Boolean(
                              formik.touched.novelty_type_id &&
                              formik.errors.novelty_type_id,
                            )}
                            fullWidth
                            helperText={
                              formik.touched.novelty_type_id &&
                              formik.errors.novelty_type_id
                            }
                            name="novelty_type_id"
                            onBlur={formik.handleBlur}
                            onChange={handleChangeNoveltyType}
                            value={formik.values.novelty_type_id}
                            variant="filled"
                            InputProps={{
                              disableUnderline: true,
                              readOnly:
                                (isErrorProcessedOrCancelled && !canReuploadThis) ||
                                isNoveltyProcessing,
                              sx: {
                                borderRadius: '6px',
                                '.MuiInputBase-input': {
                                  paddingBottom: '16px',
                                  paddingTop: '16px',
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                },
                              },
                            }}
                          >
                            {actualEmpresaNovedades.map(actualEmpresaNovedad => (
                              <MenuItem
                                key={actualEmpresaNovedad.id}
                                value={actualEmpresaNovedad.id}
                              >
                                {`${actualEmpresaNovedad.novelty_type.code} - ${actualEmpresaNovedad.novelty_type.name}`}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={6} sx={{ py: '10px', pl: '18px' }}>
                          <Box
                            display="flex"
                            justifyContent="flex-start"
                            marginBottom="4px"
                          >
                            <InputLabel sx={{ color: 'black' }} htmlFor="periodo-select">
                              {t('fields.period')}
                            </InputLabel>
                          </Box>
                          <TextField
                            id="periodo-select"
                            select
                            error={Boolean(
                              formik.touched.period_id && formik.errors.period_id,
                            )}
                            fullWidth
                            helperText={
                              formik.touched.period_id && formik.errors.period_id
                            }
                            name="period_id"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            value={formik.values.period_id}
                            variant="filled"
                            InputProps={{
                              disableUnderline: true,
                              readOnly:
                                isErrorProcessedOrCancelled || isNoveltyProcessing,
                              sx: {
                                borderRadius: '6px',
                                '.MuiInputBase-input': {
                                  paddingBottom: '16px',
                                  paddingTop: '16px',
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                },
                              },
                            }}
                          >
                            {allEmpresaPeriodos.map(periodo => (
                              <MenuItem key={periodo.id} value={periodo.id}>
                                {periodo.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={6} sx={{ py: '10px', pl: '18px' }}>
                          <Box
                            display="flex"
                            justifyContent="flex-start"
                            marginBottom="4px"
                          >
                            <InputLabel sx={{ color: 'black' }} htmlFor="status-select">
                              {t('fields.status')}
                            </InputLabel>
                          </Box>
                          <TextField
                            id="status-select"
                            select
                            error={Boolean(formik.touched.status && formik.errors.status)}
                            fullWidth
                            helperText={formik.touched.status && formik.errors.status}
                            name="status"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            value={formik.values.status}
                            variant="filled"
                            InputProps={{
                              disableUnderline: true,
                              readOnly:
                                isErrorProcessedOrCancelled || isNoveltyProcessing,
                              sx: {
                                borderRadius: '6px',
                                '.MuiInputBase-input': {
                                  paddingBottom: '16px',
                                  paddingTop: '16px',
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                },
                              },
                            }}
                          >
                            {NOVELTY_STATUS_VALUES.map(status => (
                              <MenuItem key={status.id} value={status.value}>
                                {getStatusLabel(status.value)}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={6} sx={{ py: '10px', pl: '18px' }}>
                          <Box
                            display="flex"
                            justifyContent="flex-start"
                            marginBottom="4px"
                          >
                            <InputLabel sx={{ color: 'black' }} htmlFor="prioridad-input">
                              {t('fields.priority')}
                            </InputLabel>
                          </Box>
                          <TextField
                            id="prioridad-input"
                            error={Boolean(
                              formik.touched.priority && formik.errors.priority,
                            )}
                            fullWidth
                            helperText={formik.touched.priority && formik.errors.priority}
                            name="priority"
                            onBlur={formik.handleBlur}
                            onChange={handleChangePriority}
                            type="number"
                            value={formik.values.priority}
                            variant="filled"
                            placeholder={t('fields.priorityPlaceholder')}
                            inputProps={{
                              min: 0,
                            }}
                            InputProps={{
                              disableUnderline: true,
                              readOnly:
                                isErrorProcessedOrCancelled || isNoveltyProcessing || !isBDOAdmin,
                              sx: {
                                borderRadius: '6px',
                                ...(!isBDOAdmin && { backgroundColor: 'rgba(0,0,0,0.12)' }),
                                '.MuiInputBase-input': {
                                  paddingBottom: '16px',
                                  paddingTop: '16px',
                                },
                              },
                            }}
                          />
                        </Grid>

                        {}
                        <Grid item xs={6} sx={{ py: '10px', pl: '18px' }}>
                          <Box
                            display="flex"
                            justifyContent="flex-start"
                            marginBottom="4px"
                          >
                            <InputLabel sx={{ color: 'black' }} htmlFor="novedad-input">
                              {t('fields.novelty')}
                            </InputLabel>
                          </Box>
                          <TextField
                            id="novedad-input"
                            error={Boolean(
                              formik.touched.fileName && formik.errors.fileName,
                            )}
                            fullWidth
                            helperText={formik.touched.fileName && formik.errors.fileName}
                            name="fileName"
                            value={formik.values.fileName}
                            variant="filled"
                            placeholder={t('fields.noveltyPlaceholder')}
                            InputProps={{
                              endAdornment: previusState.file && (
                                <>
                                  <InputAdornment position="end">
                                    {(!isErrorProcessedOrCancelled || canReuploadThis) && (
                                      <HtmlTooltip
                                        title={<b>{t('fields.uploadFileTooltip')}</b>}
                                      >
                                        <IconButton
                                          aria-label={t('fields.uploadFileAria')}
                                          component="label"
                                        >
                                          <input
                                            hidden
                                            id="importar-plantilla-icon-button"
                                            type="file"
                                            name="file"
                                            onChange={(e: any) =>
                                              handleUploadFile(e.currentTarget.files[0])
                                            }
                                            accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                          />
                                          <MaterialIcons.FileUploadIcon color="primary" />
                                        </IconButton>
                                      </HtmlTooltip>
                                    )}
                                    {isEqual(formik.values.file, previusState.file) && (
                                      <HtmlTooltip
                                        title={<b>{t('fields.downloadTooltip')}</b>}
                                      >
                                        <span>
                                          <IconButton
                                            aria-label={t('fields.downloadTemplateAria')}
                                            onClick={() => downloadArch()}
                                            disabled={disabledDownload}
                                          >
                                            <MaterialIcons.FileDownloadIcon
                                              color={
                                                disabledDownload ? 'disabled' : 'primary'
                                              }
                                            />
                                          </IconButton>
                                        </span>
                                      </HtmlTooltip>
                                    )}
                                  </InputAdornment>
                                </>
                              ),
                              readOnly: true,
                              disableUnderline: true,
                              sx: {
                                borderRadius: '6px',
                                '.MuiInputBase-input': {
                                  paddingBottom: '16px',
                                  paddingTop: '16px',
                                },
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item xs={2} sx={{ pb: '24px', pt: '10px', pl: '18px' }}>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="urgent"
                                  checked={formik.values.urgent}
                                  onChange={formik.handleChange}
                                  inputProps={{
                                    'aria-label': 'urgente-checkbox',
                                  }}
                                  disabled={isErrorProcessedOrCancelled}
                                />
                              }
                              sx={{
                                '.Mui-disabled.MuiTypography-root': {
                                  color: 'black',
                                },
                                '.Mui-disabled.MuiCheckbox-root': {
                                  color: theme => theme.palette.primary.main,
                                },
                              }}
                              label={t('fields.urgent')}
                            />
                          </FormGroup>
                        </Grid>
                      </Grid>
                      {!isErrorProcessedOrCancelled ||
                      isNoveltyProcessing ||
                      canReuploadThis ? (
                        <Grid container maxWidth="md" paddingBottom="32px">
                          <Box sx={{ display: 'flex', width: '100%' }}>
                            <Button
                              sx={{ marginLeft: '18px' }}
                              variant="contained"
                              color="primary"
                              onClick={handleClickGuardar}
                              id="guardar-button"
                              disabled={
                                !formik.dirty ||
                                isEqual(formik.values, previusState) ||
                                Boolean(formik.errors.fileName)
                              }
                            >
                              {t('actions.save')}
                            </Button>

                            {formik.dirty && !isEqual(formik.values, previusState) ? (
                              <Button
                                sx={{ marginLeft: '18px' }}
                                variant="outlined"
                                color="primary"
                                onClick={handleClickDirtyCancelar}
                                id="cancelar-edicion-modal-button"
                              >
                                {t('actions.cancelEdit')}
                              </Button>
                            ) : (
                              <Button
                                sx={{ marginLeft: '18px' }}
                                variant="outlined"
                                color="primary"
                                onClick={() => navigate(-1)}
                                id="cancelar-edicion-button"
                              >
                                {t('actions.cancelEdit')}
                              </Button>
                            )}
                            {!isErrorProcessedOrCancelled &&
                              previusState.status === 0 &&
                              user?.type === 1 &&
                              !isNoveltyProcessing && (
                                <HtmlTooltip
                                  title={
                                    requiresApproval ? (
                                      <b>{t('approval.requiresApprovalTooltip')}</b>
                                    ) : (
                                      ''
                                    )
                                  }
                                >
                                  <span>
                                    <Button
                                      sx={{ marginLeft: '18px' }}
                                      variant="contained"
                                      color="primary"
                                      onClick={handleClickProcesar}
                                      id="procesar-button"
                                      disabled={requiresApproval}
                                    >
                                      {t('actions.processNow')}
                                    </Button>
                                  </span>
                                </HtmlTooltip>
                              )}
                            {previusState.status === 0 && !isNoveltyProcessing && (
                              <Button
                                sx={{ marginLeft: '18px' }}
                                variant="contained"
                                color="error"
                                onClick={handleClickCancelNoveltyProccessing}
                                id="cancelar-procesar-button"
                              >
                                {t('actions.cancelProcessing')}
                              </Button>
                            )}
                            {previusState.status === 3 && userCanUnblock && (
                              <Button
                                sx={{ marginLeft: '18px' }}
                                variant="contained"
                                color="secondary"
                                onClick={handleClickUnblockNovelty}
                                id="desbloquear-button"
                              >
                                {t('actions.unblock')}
                              </Button>
                            )}
                            {previusState.status === 6 &&
                              userCanApprove &&
                              hasPayrollConfig &&
                              selectedEmpresa.payrollConfig?.requiresNoveltyApproval ===
                                true &&
                              user?.auth0Roles?.includes('cliente admin') && (
                                <Box sx={{ marginLeft: 'auto', display: 'flex' }}>
                                  <Button
                                    sx={{ marginLeft: '18px' }}
                                    variant="contained"
                                    color="success"
                                    onClick={handleClickApprove}
                                    id="aprobar-button"
                                  >
                                    {t('actions.approve')}
                                  </Button>
                                  <Button
                                    sx={{ marginLeft: '18px' }}
                                    variant="contained"
                                    color="error"
                                    onClick={handleClickReject}
                                    id="rechazar-button"
                                  >
                                    {t('actions.reject')}
                                  </Button>
                                </Box>
                              )}
                          </Box>
                        </Grid>
                      ) : (
                        <Grid container maxWidth="md" paddingBottom="32px">
                          <Button
                            sx={{ marginLeft: '18px' }}
                            variant="outlined"
                            color="primary"
                            onClick={() => navigate(-1)}
                            id="cancelar-edicion-button"
                          >
                            {t('actions.back')}
                          </Button>
                        </Grid>
                      )}
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>

      {!isLoading &&
        !fetchError &&
        !loadNoveltyError &&
        (formik.values.approved_by ||
          formik.values.status === 6 ||
          formik.values.status === 7) && (
          <>
            <Grid bgcolor="#EEF1FC" container>
              <Box
                sx={{
                  display: 'flex',
                  placeItems: 'center',
                  justifyContent: 'flex-start',
                  py: '30px',
                }}
              >
                <Typography
                  sx={{ fontSize: '24px', fontWeight: 500, lineHeight: '24px' }}
                >
                  {t('sections.approval')}
                </Typography>
              </Box>
            </Grid>

            <Grid container borderRadius="6px">
              <Grid bgcolor="white" item xs={8} borderRadius="6px">
                <Box sx={{ p: '18px' }}>
                  <Stack spacing={0.5}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography color="text.secondary">
                        {t('approval.statusLabel')}
                      </Typography>
                      <Chip
                        label={
                          formik.values.status === 6
                            ? t('approval.pendingApprovalChip')
                            : formik.values.status === 7
                              ? t('approval.rejectedChip')
                              : t('approval.approvedChip')
                        }
                        size="small"
                        color={
                          formik.values.status === 6
                            ? 'warning'
                            : formik.values.status === 7
                              ? 'error'
                              : 'success'
                        }
                      />
                    </Box>

                    {formik.values.created_by_user && (
                      <Box display="flex" alignItems="baseline" gap={1}>
                        <Typography color="text.secondary">
                          {t('approval.uploadedBy')}
                        </Typography>
                        <Typography fontWeight={500}>
                          {formik.values.created_by_user.given_name}{' '}
                          {formik.values.created_by_user.family_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ({formik.values.created_by_user.email})
                        </Typography>
                      </Box>
                    )}

                    {formik.values.approved_by && (
                      <>
                        <Box display="flex" alignItems="baseline" gap={1}>
                          <Typography color="text.secondary">
                            {formik.values.status === 7
                              ? t('approval.rejectedBy')
                              : t('approval.approvedBy')}
                          </Typography>
                          <Typography fontWeight={500}>
                            {formik.values.approved_by.given_name}{' '}
                            {formik.values.approved_by.family_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ({formik.values.approved_by.email})
                          </Typography>
                        </Box>

                        {formik.values.approved_at && (
                          <Box display="flex" alignItems="baseline" gap={1}>
                            <Typography color="text.secondary">
                              {t('approval.date')}
                            </Typography>
                            <Typography fontWeight={500}>
                              {dayjs(formik.values.approved_at).format(
                                'DD/MM/YYYY HH:mm:ss',
                              )}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}

                    {formik.values.status === 7 && formik.values.rejection_reason && (
                      <Box display="flex" alignItems="baseline" gap={1}>
                        <Typography color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {t('approval.rejectionReason')}
                        </Typography>
                        <Typography
                          fontWeight={500}
                          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {formik.values.rejection_reason}
                        </Typography>
                      </Box>
                    )}

                    {formik.values.status === 6 && !formik.values.approved_by && (
                      <Typography variant="body2" color="text.secondary">
                        {t('approval.pendingApprovalNotice')}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </>
        )}

      {!isLoading && !fetchError && !loadNoveltyError && (
        <>
          <Grid bgcolor="#EEF1FC" container>
            <Box
              sx={{
                display: 'flex',
                placeItems: 'center',
                justifyContent: 'flex-start',
                py: '30px',
              }}
            >
              <Typography sx={{ fontSize: '24px', fontWeight: 500, lineHeight: '24px' }}>
                {t('sections.result')}
              </Typography>
            </Box>
          </Grid>

          <Grid container borderRadius="6px">
            <Grid bgcolor="white" item xs={8} borderRadius="6px">
              <Box sx={{ p: '18px' }}>
                <Stack spacing={0.5}>
                  {formik.values.created_at && (
                    <Box display="flex" alignItems="baseline" gap={1}>
                      <Typography color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {t('result.loadDate')}
                      </Typography>
                      <Typography fontWeight={500}>
                        {dayjs(formik.values.created_at).format('DD/MM/YYYY HH:mm:ss')}
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" alignItems="baseline" gap={1}>
                    <Typography color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {t('result.processDate')}
                    </Typography>
                    <Typography fontWeight={500}>
                      {formik.values.process_date
                        ? dayjs(formik.values.process_date).format('DD/MM/YYYY HH:mm:ss')
                        : t('result.notProcessed')}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="baseline" gap={1}>
                    <Typography color="text.secondary">
                      {t('result.resultLabel')}
                    </Typography>
                    {formik.values.error && (
                      <Typography
                        fontWeight={500}
                        sx={{
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {formik.values.error}
                      </Typography>
                    )}
                    {formik.values.result && (
                      <Typography fontWeight={500}>{formik.values.result}</Typography>
                    )}
                  </Box>
                  {(formik.values.error || formik.values.result) && (
                    <Box sx={{ pt: '8px' }}>
                      <Button
                        color="primary"
                        variant="contained"
                        startIcon={<MaterialIcons.DescriptionIcon />}
                        onClick={() => getNovedadLogs()}
                        disabled={disabledDownloadLogs}
                      >
                        {t('actions.downloadLogs')}
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </>
      )}

      {openUnblockModal && (
        <InformationModal
          title={t('modals.unblockTitle')}
          message={t('modals.unblockMessage')}
          openModal={openUnblockModal}
          onClose={handleCloseModal}
          saveForm={handleSaveForm}
          isDirty={false}
          clickCancelar={clickCancelar}
          guardar={clickDesbloquear}
          guardarMessage={t('modals.saveConfirmMessage')}
          clickEliminar={false}
          disabledOnSubmit={disabledOnSubmit}
        />
      )}

      {openInformationModal && (
        <InformationModal
          title={isDirty ? t('modals.cancelChangesTitle') : t('modals.editTitle')}
          message={
            isDirty ? t('modals.cancelChangesMessage') : t('modals.editMessage')
          }
          openModal={openInformationModal}
          onClose={handleCloseModal}
          saveForm={handleSaveButton}
          isDirty={isDirty}
          clickCancelar={clickCancelar}
          handleDeshacerCancelar={handleDeshacerCancelar}
          guardar={clickGuardar}
          guardarMessage={t('modals.saveConfirmMessage')}
          clickEliminar={false}
          disabledOnSubmit={disabledOnSubmit}
        />
      )}

      {openProcessingModal && (
        <InformationModal
          title={
            clickCancelarProcesamiento
              ? t('modals.cancelProcessingTitle')
              : t('modals.processTitle')
          }
          message={
            clickCancelarProcesamiento
              ? t('modals.cancelProcessingMessage')
              : t('modals.processMessage')
          }
          openModal={openProcessingModal}
          onClose={handleIsProcessingCloseModal}
          saveForm={handleSaveForm}
          isDirty={clickCancelarProcesamiento}
          clickCancelar={false}
          handleDeshacerCancelar={handleDeshacerCancelar}
          guardar={clickProcesar || clickCancelarProcesamiento}
          guardarMessage={t('modals.processMessage')}
          clickEliminar={false}
          disabledOnSubmit={disabledOnSubmit}
          showIcon={showIcon}
          successIcon={successIcon}
          processingErrorMessage={errorMessage}
          isProcessingMessage={isProcessingMessage}
        />
      )}

      {openErrorModal && (
        <InformationModal
          title={t('modals.errorTitle')}
          message={errorMessage}
          onClose={handleCloseModal}
          openModal={openErrorModal}
          clickCancelar={false}
          clickEliminar={false}
          guardar={false}
          isDirty={false}
          errorMessage
        />
      )}

      {openApproveModal && (
        <InformationModal
          title={t('modals.approveTitle')}
          message={t('modals.approveMessage')}
          openModal={openApproveModal}
          onClose={handleCloseModal}
          saveForm={handleApproveNovelty}
          isDirty={false}
          clickCancelar={false}
          handleDeshacerCancelar={handleCloseModal}
          guardar={true}
          guardarMessage={t('modals.approveConfirmMessage')}
          clickEliminar={false}
          disabledOnSubmit={isProcessing}
          showIcon={showIcon}
          successIcon={successIcon}
          isProcessingMessage={isProcessingMessage}
        />
      )}

      {openRejectModal && (
        <InformationModal
          title={t('modals.rejectTitle')}
          message={t('modals.rejectMessage')}
          openModal={openRejectModal}
          onClose={handleCloseModal}
          saveForm={handleRejectNovelty}
          isDirty={false}
          clickCancelar={false}
          handleDeshacerCancelar={handleCloseModal}
          guardar={true}
          guardarMessage={t('modals.rejectConfirmMessage')}
          clickEliminar={false}
          disabledOnSubmit={isProcessing}
          showIcon={showIcon}
          successIcon={successIcon}
          isProcessingMessage={isProcessingMessage}
        >
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('modals.rejectReasonLabel')}
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
            placeholder={t('modals.rejectReasonPlaceholder')}
            required
          />
        </InformationModal>
      )}
      {openPriorityChainModal && (
        <PriorityChainModal
          openModal={openPriorityChainModal}
          companyId={empresaId}
          currentNoveltyId={params.novedadId}
          chain={priorityChain}
          onClose={() => setOpenPriorityChainModal(false)}
          onNoveltyUpdated={() => setRefreshNovelty(true)}
        />
      )}
    </Layout>
  )
}

export default NovedadDetails
